import React, { useState, useEffect, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { supabase } from "../../lib/supabase_client";
import { useCompany } from "../../contexts/CompanyContext";
import { useSession } from "../../contexts/SessionContext";
import '../../styles/page-base.css';

interface Role {
  id: number;
  role_name: string;
}

interface Permission {
  id: number;
  name: string;
  description?: string;
  category?: string;
}

interface PermissionMatrix {
  [key: string]: {
    [roleId: number]: boolean;
  };
}

type PermissionCategory = 'Settings' | 'Usage logs' | 'Team' | 'Wallet' | 'File';

export default function AdminRolePermission() {
  const [permissions, setPermissions] = useState<PermissionMatrix>({});
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionList, setPermissionList] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<PermissionCategory>('File');
  const { selectedCompany } = useCompany();
  const { refresh } = useSession();

  // Get unique categories from permissions, sorted
  const categories = Array.from(
    new Set(permissionList.map(p => p.category).filter(Boolean))
  ).sort() as PermissionCategory[];

  // Get permissions for active tab, sorted by name
  const activeTabPermissions = permissionList
    .filter(p => p.category === activeTab)
    .sort((a, b) => a.name.localeCompare(b.name));

  const fetchRolesAndPermissions = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('roles')
        .select('id, role_name')
        .order('id');
  
      // Fetch permissions with category
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('permissions')
        .select('id, name, description, category')
        .order('category, name');
  
      if (rolesError || permissionsError) {
        console.error('Error fetching data:', rolesError || permissionsError);
        return;
      }

      setRoles(rolesData || []);
      setPermissionList(permissionsData || []);

      // Initialize permissions matrix with false
      const initialMatrix: PermissionMatrix = {};
      (permissionsData || []).forEach(permission => {
        initialMatrix[permission.name] = {};
        (rolesData || []).forEach(role => {
          initialMatrix[permission.name][role.id] = false;
        });
      });
  
      // Fetch GLOBAL permissions (org_id = null)
      const { data: globalPermissionsData, error: globalPermissionsError } = await supabase
        .from('role_permissions')
        .select(`
            allowed,
            roles:role_id ( id, role_name ),
            permissions:permission_id ( id, name )
        `)
        .is('org_id', null);

      if (!globalPermissionsError && globalPermissionsData?.length) {
        globalPermissionsData.forEach((rp: any) => {
          const roleId = rp.roles?.id;
          const permissionName = rp.permissions?.name?.trim();
      
          if (!roleId || !permissionName) return;
      
          if (
            initialMatrix[permissionName] &&
            roleId in initialMatrix[permissionName]
          ) {
            initialMatrix[permissionName][roleId] = rp.allowed;
          }
        });
      }
          
      // Fetch organization-specific permissions if they exist (override global)
      if (selectedCompany?.company_id) {
        const { data: orgPermissionsData, error: orgPermissionsError } = await supabase
            .from('role_permissions')
            .select(`
            allowed,
            roles:role_id ( id, role_name ),
            permissions:permission_id ( id, name )
            `)
            .eq('org_id', selectedCompany.company_id);
  
        if (!orgPermissionsError && orgPermissionsData) {
          orgPermissionsData.forEach(rp => {
            const roleId = (rp.roles as any)?.id;
            const permissionName = (rp.permissions as any)?.name || '';
            if (initialMatrix[permissionName] && initialMatrix[permissionName][roleId] !== undefined) {
              initialMatrix[permissionName][roleId] = rp.allowed;
            }
          });
        }
      }
  
      setPermissions(initialMatrix);
    } catch (error) {
      console.error('Error fetching roles and permissions:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.company_id]);

  // Set first category as default when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !categories.includes(activeTab)) {
      setActiveTab(categories[0] as PermissionCategory);
    }
  }, [permissionList, activeTab]);

  useEffect(() => {
    fetchRolesAndPermissions();
  }, [selectedCompany?.company_id, fetchRolesAndPermissions]);

  const resetToDefault = async () => {
    if (!selectedCompany?.company_id) {
      alert('No organization selected');
      return;
    }
  
    try {
      setLoading(true);
      
      const { error: deleteError } = await supabase
        .from('role_permissions')
        .delete()
        .eq('org_id', selectedCompany.company_id);
  
      if (deleteError) {
        console.error('Error deleting org permissions:', deleteError);
        alert('Failed to reset permissions. Please try again.');
        return;
      }
  
      await fetchRolesAndPermissions();
      await refresh();
      
      alert('Permissions reset to global defaults successfully!');
    } catch (error) {
      console.error('Error resetting permissions:', error);
      alert('Failed to reset permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePermissionChange = (permissionName: string, roleId: number, value: boolean) => {
    setPermissions(prev => ({
      ...prev,
      [permissionName]: {
        ...prev[permissionName],
        [roleId]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedCompany?.company_id) {
      alert('No organization selected');
      return;
    }
  
    try {
      setSaving(true);
  
      // Get global permissions for comparison
      const { data: globalPermissions } = await supabase
        .from('role_permissions')
        .select(`
          role_id,
          permission_id,
          allowed,
          roles:role_id ( id, role_name ),
          permissions:permission_id ( id, name )
        `)
        .is('org_id', null);
  
      const globalMap = new Map();
      (globalPermissions || []).forEach(g => {
        const roleId = (g.roles as any)?.id;
        const permissionName = (g.permissions as any)?.name?.trim();
        if (roleId && permissionName) {
          globalMap.set(`${roleId}_${permissionName}`, g.allowed);
        }
      });
  
      // Prepare org-specific data (only diffs)
      const upsertData: any[] = [];
      const permissionMap = new Map(permissionList.map(p => [p.name, p.id]));
  
      Object.entries(permissions).forEach(([permName, rolePerms]) => {
        Object.entries(rolePerms).forEach(([roleIdStr, allowed]) => {
          const roleId = parseInt(roleIdStr);  
          const permId = permissionMap.get(permName);
          if (!roleId || !permId) return;
      
          const globalAllowed = globalMap.get(`${roleId}_${permName}`);
          const effectiveGlobalAllowed = globalAllowed !== undefined ? globalAllowed : false;
          
          if (allowed != effectiveGlobalAllowed) {
            upsertData.push({
              role_id: roleId,
              permission_id: permId,
              org_id: selectedCompany.company_id,
              allowed
            });
          }
        });
      });
  
      const {data: existingPermissions} = await supabase
        .from('role_permissions')
        .select('id, role_id, permission_id, allowed')
        .eq('org_id', selectedCompany.company_id);

      const deletions: any[] = [];
      if (existingPermissions) {
        existingPermissions.forEach(existing => {
            const roleId = existing.role_id;
            const permission = permissionList.find(p => p.id === existing.permission_id);

            if (roleId && permission) {
                const permName = permission.name;
                const currAllowed = permissions[permName]?.[roleId];
                const globalAllowed = globalMap.get(`${roleId}_${permName}`);
                const effectiveGlobalAllowed = globalAllowed !== undefined ? globalAllowed : false;

                if (currAllowed === effectiveGlobalAllowed) {
                    deletions.push(existing.id);
            }}
        });
      }

      if (upsertData.length === 0 && deletions.length === 0) {
        alert('No changes detected from global permissions.');
        return;
      }

      if (deletions.length > 0) {
        const { error: deleteError } = await supabase
          .from('role_permissions')
          .delete()
          .in('id', deletions);
        
        if (deleteError) {
          console.error('Error deleting permissions:', deleteError);
          alert('Failed to delete some permissions. Please try again.');
          return;
        }
      }

      if (upsertData.length > 0) {
        const { error } = await supabase
          .from('role_permissions')
          .upsert(upsertData, { onConflict: 'role_id,permission_id,org_id' });
      
        if (error) {
          console.error('Error saving permissions:', error);
          alert('Failed to save permissions. Please try again.');
          return;
        }
      }
      alert('Permissions saved successfully!');
      await refresh();
    } catch (error) {
      console.error('Error saving permissions:', error);
      alert('Failed to save permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionDisplayName = (permissionName: string): string => {
    // Remove category prefix and format nicely
    const parts = permissionName.split('_');
    if (parts.length > 1) {
      // Remove the category prefix (first part)
      const actionParts = parts.slice(1);
      return actionParts
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return permissionName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderTabContent = () => {
    return (
      <div className="page-card">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="page-table">
              <thead className="page-table-thead">
                <tr className="page-table-tr">
                  <th className="page-table-th">
                    Permission
                  </th>
                  {roles.map(role => (
                    <th key={role.id} className="page-table-th">
                      <div className="page-table-td-content">
                        <span className="page-table-td-content-text">{role.role_name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="page-table-tbody">
                {activeTabPermissions.length === 0 ? (
                  <tr>
                    <td colSpan={roles.length + 1} className="page-table-td text-center text-gray-500 py-8">
                      No permissions found for this category.
                    </td>
                  </tr>
                ) : (
                  activeTabPermissions.map((permission) => (
                    <tr
                      key={permission.id}
                      className="page-table-tr"
                    >
                      <td className="page-table-td">
                        <div className="page-table-td-content">
                          {permission.description || getPermissionDisplayName(permission.name)}
                        </div>
                      </td>
                      {roles.map(role => (
                        <td key={role.id} className="page-table-td">
                          <div className="page-table-td-content">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={permissions[permission.name]?.[role.id] || false}
                                onChange={(e) => handlePermissionChange(
                                  permission.name, 
                                  role.id, 
                                  e.target.checked
                                )}
                                className="sr-only peer"
                              />
                              <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                                permissions[permission.name]?.[role.id] 
                                  ? 'border-blue-400 bg-white' 
                                  : 'border-gray-300 bg-white'
                              }`}>
                                {permissions[permission.name]?.[role.id] && (
                                  <svg 
                                    className="w-3.5 h-3.5 text-blue-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="3"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </label>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
    <div className="page-container">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="page-header">
        <div className="page-header-left">
        <div className="text-sm text-gray-500 mb-1">Admin Only</div>
            <h1 className="page-title">Permissions</h1>
        </div>

        {/* Action Buttons */}
        <div className="page-header-right">
          <div className="flex items-center space-x-3">
            <button 
              onClick={resetToDefault}
              className="f-btn f-btn-primary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Reset to Default</span>
            </button>
          </div>
          
          <button 
            onClick={handleSavePermissions}
            disabled={saving}
            className="f-btn f-btn-success"
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
        </div>

        {/* Tab Navigation - Same style as Settings page */}
        <div className="page-tabs">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveTab(category as PermissionCategory)}
                className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === category
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {category}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {renderTabContent()}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">How it works</h3>
          <div className="text-xs text-blue-700 space-y-1">
            <div>• <span className="font-medium">Granular Permissions:</span> Each permission is a single action that can be independently enabled/disabled</div>
            <div>• <span className="font-medium">Categories:</span> Permissions are organized by category (Settings, Usage logs, Team, Wallet, File)</div>
            <div>• <span className="font-medium">Owner:</span> Has all permissions enabled by default</div>
            <div>• <span className="font-medium">Organization-specific:</span> Custom permissions override global defaults for this organization</div>
          </div>
        </div>
      </div>
    </div>
    </MainLayout>
  );
}